import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// Reusable multi-metric area chart with toggleable series.
// metrics: [{ key, label, color }]. defaultActive: array of keys shown initially.
export function MetricChart({ title, data, metrics, defaultActive, testId }) {
  const [active, setActive] = useState(() => defaultActive || metrics.map((m) => m.key));
  const toggle = (k) =>
    setActive((a) => (a.includes(k) ? (a.length > 1 ? a.filter((x) => x !== k) : a) : [...a, k]));

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={testId}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-toggles`}>
          {metrics.map((m) => {
            const on = active.includes(m.key);
            return (
              <button
                key={m.key}
                type="button"
                data-testid={`metric-toggle-${m.key}`}
                onClick={() => toggle(m.key)}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  on ? "border-white/20 bg-white/[0.06] text-foreground" : "border-white/[0.08] text-muted-foreground/50 hover:text-muted-foreground"
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: on ? m.color : "transparent", border: `1px solid ${m.color}` }}
                />
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data || []} margin={{ left: -10, right: 8, top: 4 }}>
          <defs>
            {metrics.map((m) => (
              <linearGradient key={m.key} id={`grad-${testId}-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={m.color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={m.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="date" stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: "#1a1a1e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: "#A1A1AA" }}
          />
          {metrics
            .filter((m) => active.includes(m.key))
            .map((m) => (
              <Area
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={2}
                fill={`url(#grad-${testId}-${m.key})`}
              />
            ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
